import React, { useState } from "react";
import ReactDOM from "react-dom";
import { Modal } from "react-bootstrap";

const formatClock = (iso) => {
  if (!iso) return "";
  const date = new Date(iso);
  return date.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

const formatRemaining = (deadlineIso, serverNow) => {
  const remaining = Math.max(0, Math.ceil((Date.parse(deadlineIso) - serverNow) / 1000));
  const minutes = Math.floor(remaining / 60);
  const seconds = String(remaining % 60).padStart(2, "0");
  return `${minutes}:${seconds}`;
};

const Portal = ({ children }) => ReactDOM.createPortal(children, document.body);

const CollapseButton = ({ onCollapse }) => (
  <button
    type="button"
    className="impersonation-collapse-btn"
    onClick={onCollapse}
    aria-label="Hide impersonation bar"
    title="Hide impersonation bar"
  >
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M7 14l5-5 5 5z" fill="currentColor" />
    </svg>
  </button>
);

export const AdminImpersonationBar = ({ session, busy, onExit, onCollapse }) => (
  <div className="impersonation-bar" role="alert">
    <span className="impersonation-bar__text">
      You (<strong>{session.requester?.name}</strong>) are signed in as <strong>{session.target?.name}</strong>
      {session.started_at && <> · since {formatClock(session.started_at)}</>}
    </span>
    <button type="button" className="impersonation-exit-btn" onClick={onExit} disabled={busy}>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M5 21C4.45 21 3.97917 20.8042 3.5875 20.4125C3.19583 20.0208 3 19.55 3 19V5C3 4.45 3.19583 3.97917 3.5875 3.5875C3.97917 3.19583 4.45 3 5 3H12V5H5V19H12V21H5ZM16 17L14.625 15.55L17.175 13H9V11H17.175L14.625 8.45L16 7L21 12L16 17Z"
          fill="currentColor"
        />
      </svg>
      Exit impersonation
    </button>
    <CollapseButton onCollapse={onCollapse} />
  </div>
);

export const AccessingMeBar = ({ session, busy, onEnd, onCollapse }) => (
  <div className="impersonation-bar impersonation-bar--target" role="alert">
    <span className="impersonation-bar__text">
      <strong>{session.requester?.name}</strong> ({session.requester?.email}) is accessing your account
      {session.started_at && <> since {formatClock(session.started_at)}</>}
    </span>
    <button type="button" className="impersonation-exit-btn" onClick={onEnd} disabled={busy}>
      End access
    </button>
    <CollapseButton onCollapse={onCollapse} />
  </div>
);

export const RequestReasonModal = ({ target, consentBypass, busy, onSubmit, onClose }) => {
  const [reason, setReason] = useState("");
  const name = target.name || target.full_name || target.email;

  return (
    <Modal
      show
      onHide={onClose}
      centered
      className="impersonation-bs-modal"
      backdropClassName="impersonation-bs-backdrop"
      contentClassName="impersonation-modal"
    >
      <h5 className="impersonation-modal__title">{consentBypass ? `Access ${name}'s account` : `Request access to ${name}'s account`}</h5>
      <p className="impersonation-modal__text">
        {consentBypass
          ? `Super admin access: ${name} will not be asked to approve. After you continue, click Enter within 5 minutes. They will see that you are accessing their account and can end it.`
          : `${name} will be asked to approve your request. The request expires if they don't respond within 3 minutes.`}
      </p>
      <label className="impersonation-modal__label" htmlFor="impersonation-reason">Reason (optional, shown to the user)</label>
      <textarea
        id="impersonation-reason"
        className="impersonation-modal__textarea"
        value={reason}
        maxLength={500}
        rows={3}
        autoFocus
        onChange={(event) => setReason(event.target.value)}
      />
      <div className="impersonation-modal__actions">
        <button type="button" className="impersonation-btn impersonation-btn--secondary" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="impersonation-btn impersonation-btn--primary" onClick={() => onSubmit(reason)} disabled={busy}>
          {consentBypass ? "Continue" : "Send request"}
        </button>
      </div>
    </Modal>
  );
};

export const IncomingRequestModal = ({ request, serverNow, busy, onAccept, onDecline }) => (
  <Portal>
    <div className="impersonation-modal-backdrop">
      <div className="impersonation-modal" role="dialog" aria-modal="true">
        <h5 className="impersonation-modal__title">Account access request</h5>
        <p className="impersonation-modal__text">
          <strong>{request.requester?.name}</strong> ({request.requester?.email}) wants to access your account to view and act as you.
        </p>
        {request.reason && (
          <p className="impersonation-modal__reason">
            <span>Reason:</span> {request.reason}
          </p>
        )}
        <p className="impersonation-modal__hint">
          You can end their access at any time from the bar at the top of the page. This request expires in {formatRemaining(request.expires_at, serverNow)}.
        </p>
        <div className="impersonation-modal__actions">
          <button type="button" className="impersonation-btn impersonation-btn--secondary" onClick={onDecline} disabled={busy}>
            Decline
          </button>
          <button type="button" className="impersonation-btn impersonation-btn--primary" onClick={onAccept} disabled={busy}>
            Accept
          </button>
        </div>
      </div>
    </div>
  </Portal>
);

export const OutgoingRequestCard = ({ request, serverNow, busy, onCancel, onEnter }) => {
  const name = request.target?.name || "the user";
  const accepted = request.status === "accepted";

  return (
    <Portal>
      <div className="impersonation-card" role="status">
        {accepted ? (
          <>
            <div className="impersonation-card__text">
              {request.consent_bypassed
                ? <>Access to <strong>{name}</strong>'s account is ready. Enter within {formatRemaining(request.enter_expires_at, serverNow)}.</>
                : <><strong>{name}</strong> accepted your request. Enter within {formatRemaining(request.enter_expires_at, serverNow)}.</>}
            </div>
            <div className="impersonation-card__actions">
              <button type="button" className="impersonation-btn impersonation-btn--secondary" onClick={onCancel} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="impersonation-btn impersonation-btn--primary" onClick={onEnter} disabled={busy}>
                Enter
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="impersonation-card__text">
              Waiting for <strong>{name}</strong> to accept · {formatRemaining(request.expires_at, serverNow)}
            </div>
            <div className="impersonation-card__actions">
              <button type="button" className="impersonation-btn impersonation-btn--secondary" onClick={onCancel} disabled={busy}>
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </Portal>
  );
};

export const ToastStack = ({ toasts }) => {
  if (!toasts.length) return null;
  return (
    <Portal>
      <div className="impersonation-toasts">
        {toasts.map((toast) => (
          <div key={toast.id} className={`impersonation-toast impersonation-toast--${toast.variant}`} role="status">
            {toast.message}
          </div>
        ))}
      </div>
    </Portal>
  );
};
